import * as React from 'react';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

import {
  styled,
  styledDiv,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {FinePrint, Subheading} from '@packages/systems/design-system/Text';
import Grid from '@packages/systems/design-system/Grid';
import Counter from '@packages/systems/design-system/Counter';
import Button from '@packages/systems/design-system/Button';
import {Link} from '@packages/systems/design-system/Link';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';

const Divider = styledDiv(
  ({theme}) => ({
    height: 1,
    width: '100%',
    backgroundColor: theme.colors.border,
  }),
  {
    darker: ({theme: {colors}}) => ({
      backgroundColor: colors.logicFlows.blockBorderColor,
    }),
  }
);
Divider.displayName = 'Divider';

const StyledButton = styled(Button)(({theme: {gutters}}) => ({
  paddingLeft: gutters.x8,
  paddingRight: gutters.x8,
}));

const Wrapper = styledDiv(({theme: {colors, control, gutters}, style}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: gutters.x12,
  alignItems: 'center',
  padding: gutters.x16,
  backgroundColor: colors.backgroundVivid,
  border: `${control.borderWidth}px solid ${colors.border}`,
  borderRadius: control.borderRadius,
  ...style,
}));

const ContentWrapper = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.space[1],
  alignItems: 'center',
  textAlign: 'center',
});

const CounterContainer = styledDiv(({theme: {gutters}}) => ({
  display: 'flex',
  justifyContent: 'end',
  width: gutters.x16,
  height: gutters.x16,
}));
const StyledCounter = styled(Counter)(({theme: {colors, gutters}}) => ({
  background: colors.foregroundDim,
  alignSelf: 'start',
  alignItems: 'center',
  minWidth: gutters.x12,
  width: gutters.x12,
  height: gutters.x12,
  marginTop: gutters.x4 / 2,
  paddingLeft: 0,
  paddingRight: 0,
}));

const StyledListText = styled(FinePrint)({
  textAlign: 'left',
});

const StyledLink = styled(Link)(({theme}) => ({
  fontSize: theme.fontSizes.copy,
}));

type Props = {
  /** Heading label */
  heading: string;
  /** Heading icon */
  headingIcon?: React.ReactNode;
  /** Content to render. List of strings will render an ordered list. Typically used for step-by-step instructions */
  content: string | Array<string>;
  /** Automation ID for E2E tests (e.g. Cypress) */
  ['data-automation-id']?: string;
  /** Optional call to action. Can be a link, button, or some custom UI */
  callToAction?:
    | {
        type: 'link';
        href: string;
        label: string;
        target?: string;
        rel?: string;
      }
    | {
        type: 'button';
        label: React.ReactNode;
        onClick: () => void;
        intent?: 'primary' | 'default';
        dataAutomationId?: string;
        tip?: string;
        disabled?: boolean;
      }
    | {
        type: 'custom';
        render: () => React.ReactElement;
      };
  style?: CSSProperties;
};

export function LogicEmptyState({
  heading,
  headingIcon,
  content,
  callToAction,
  style,
  'data-automation-id': dataAutomationId,
}: Props) {
  return (
    <Wrapper data-automation-id={dataAutomationId} style={style}>
      {headingIcon ? headingIcon : null}

      <ContentWrapper>
        <Subheading vivid>{heading}</Subheading>
        {Array.isArray(content) ? (
          <Grid gridRowGap={2}>
            {content.map((text, index) => (
              <Grid
                gridTemplateColumns="16px auto"
                key={index}
                gridColumnGap={1}
              >
                <CounterContainer>
                  <StyledCounter count={index + 1} />
                </CounterContainer>
                <StyledListText dim>{text}</StyledListText>
              </Grid>
            ))}
          </Grid>
        ) : (
          <FinePrint dim>{content}</FinePrint>
        )}
      </ContentWrapper>

      <RenderCallToAction callToAction={callToAction} />
    </Wrapper>
  );
}

function RenderCallToAction({
  callToAction,
}: {
  callToAction: Props['callToAction'];
}) {
  if (callToAction?.type === 'link') {
    return (
      <StyledLink
        primary
        isExternal
        href={callToAction.href}
        target={callToAction.target}
        rel={callToAction.rel}
      >
        {callToAction.label}
      </StyledLink>
    );
  }

  if (callToAction?.type === 'button') {
    return (
      <TooltipTrigger content={callToAction.tip} alternate>
        <StyledButton
          data-automation-id={callToAction.dataAutomationId}
          primary={callToAction.intent === 'primary'}
          disabled={callToAction.disabled}
          onClick={callToAction.onClick}
        >
          {callToAction.label}
        </StyledButton>
      </TooltipTrigger>
    );
  }

  if (callToAction?.type === 'custom') {
    return callToAction.render();
  }

  return null;
}
